import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Tooltip } from 'antd';
import { DeviceActivityEvent } from './hooks/usePlantSensorData';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';

/**
 * Get the hour position as percentage of 24 hours
 */
function getHourPosition(date: Date): number {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return ((hours * 60 + minutes) / (24 * 60)) * 100;
}

// Field labels in Danish
const FIELD_LABELS: Record<string, { name: string; description: string; color: string }> = {
  v1: { name: 'Ventil 1', description: 'Åbning af ventil 1', color: 'bg-blue-500' },
  v2: { name: 'Ventil 2', description: 'Åbning af ventil 2', color: 'bg-cyan-500' },
  ex: { name: 'Ekstern bånd', description: 'Signal fra kundens anlæg', color: 'bg-purple-500' },
  on: { name: 'Drift mode', description: 'Anlæg i drift', color: 'bg-amber-500' },
};

interface GroupedEvents {
  day: string;
  dayFormatted: string;
  events: {
    field: string;
    startPos: number;
    width: number;
    startTime: string;
    endTime: string;
    count: number;
  }[];
  totals: Record<string, number>;
}

interface DeviceActivityTimelineProps {
  deviceActivity: DeviceActivityEvent[];
  field: 'v1' | 'v2' | 'ex' | 'on';
}

export function DeviceActivityTimeline({ deviceActivity, field }: DeviceActivityTimelineProps) {
  const fieldConfig = FIELD_LABELS[field];

  const groupedData = useMemo(() => {
    if (!deviceActivity || deviceActivity.length === 0) return [];

    // Filter to specific field
    const fieldEvents = deviceActivity.filter((e) => e.field === field);
    if (fieldEvents.length === 0) return [];

    // Group by day
    const byDay = new Map<string, DeviceActivityEvent[]>();
    fieldEvents.forEach((event) => {
      const existing = byDay.get(event.day) || [];
      existing.push(event);
      byDay.set(event.day, existing);
    });

    // Transform to grouped format
    const result: GroupedEvents[] = [];
    byDay.forEach((events, day) => {
      const date = parseISO(day);
      const totals: Record<string, number> = { [field]: 0 };

      const transformedEvents = events.map((e) => {
        const endDate = parseISO(e.end_time);
        const startDate = parseISO(e.start_time);

        totals[field] += e.count_increase;

        const startPos = getHourPosition(startDate);
        const endPos = getHourPosition(endDate);
        const width = Math.max(endPos - startPos, 1); // Minimum 1% width for visibility

        return {
          field: e.field,
          startPos,
          width: Math.min(width, 100 - startPos),
          startTime: format(startDate, 'HH:mm'),
          endTime: format(endDate, 'HH:mm'),
          count: e.count_increase,
        };
      });

      result.push({
        day,
        dayFormatted: format(date, 'EEE d. MMM', { locale: da }),
        events: transformedEvents,
        totals,
      });
    });

    // Sort by day
    result.sort((a, b) => a.day.localeCompare(b.day));

    return result;
  }, [deviceActivity, field]);

  if (groupedData.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{fieldConfig.name}</CardTitle>
          <CardDescription className="text-xs">{fieldConfig.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Ingen data tilgængelig</p>
        </CardContent>
      </Card>
    );
  }

  // Hour labels for the timeline header
  const hourLabels = [0, 6, 12, 18];
  const totalCount = groupedData.reduce((sum, d) => sum + (d.totals[field] || 0), 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{fieldConfig.name}</CardTitle>
        <CardDescription className="text-xs">{fieldConfig.description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-2">
        {/* Hour labels header */}
        <div className="flex mb-1">
          <div className="w-24 shrink-0" />
          <div className="flex-1 relative h-4">
            {hourLabels.map((hour) => (
              <span
                key={hour}
                className="absolute text-[10px] text-muted-foreground"
                style={{ left: `${(hour / 24) * 100}%`, transform: 'translateX(-50%)' }}
              >
                {hour.toString().padStart(2, '0')}
              </span>
            ))}
          </div>
          <div className="w-12 shrink-0 text-right text-[10px] text-muted-foreground">Antal</div>
        </div>

        {/* Timeline rows */}
        <div className="space-y-0.5">
          {groupedData.map((dayData) => (
            <div key={dayData.day} className="flex items-center h-6">
              {/* Day label */}
              <div className="w-24 shrink-0 text-xs text-muted-foreground truncate pr-2">
                {dayData.dayFormatted}
              </div>

              {/* Timeline bar */}
              <div className="flex-1 relative h-5 bg-muted rounded">
                {/* Hour grid lines */}
                {hourLabels.map((hour) => (
                  <div
                    key={hour}
                    className="absolute h-full border-l border-border/30"
                    style={{ left: `${(hour / 24) * 100}%` }}
                  />
                ))}

                {/* Activity events */}
                {dayData.events.map((event, idx) => (
                  <Tooltip
                    key={idx}
                    title={
                      <div className="text-xs">
                        <div>
                          {event.startTime} - {event.endTime}
                        </div>
                        <div>Antal: {event.count}</div>
                      </div>
                    }
                  >
                    <div
                      className={`absolute h-full ${fieldConfig.color} rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center`}
                      style={{
                        left: `${event.startPos}%`,
                        width: `${event.width}%`,
                        minWidth: '8px',
                      }}
                    >
                      {event.count > 1 && event.width > 3 && (
                        <span className="text-[9px] text-white font-medium">{event.count}</span>
                      )}
                    </div>
                  </Tooltip>
                ))}
              </div>

              {/* Total for day */}
              <div className="w-12 shrink-0 text-right text-xs font-medium">
                {dayData.totals[field] || 0}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className={`w-2.5 h-2.5 ${fieldConfig.color} rounded`} />
            <span>Aktivering</span>
          </div>
          <div className="ml-auto">
            Total: <span className="font-medium">{totalCount}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
